import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { showError, showSuccess } from "toast/toast";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { StatusBadge } from "components/Productlocationstatus";
import { License, Office, Agent, Address, Product } from "types";
import { getAuthCookie } from "../../utils/authCookies";

type ProductObj = {
  product_id: string;
  agent_id: string;
  office_id: string;
  inventory_type: string;
  product: Product;
  linked_installer_details: any
  created_by: {
    id: string;
    name: string;
  };
};

type Selected = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "350px",
  borderRadius: "12px",
  overflow: "hidden",
  // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
  border: "1px solid #dee2e6",          // Light border
};

const center = {
  lat: 40.712776, // Default latitude (e.g., New York City)
  lng: -74.005974, // Default longitude
};

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageEditInstallation = () => {
  const { installationId } = useParams<{ installationId: string }>();
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
  const [installer, setInstaller] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [quantity, setQuantity] = useState(1);
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showViewInventoryModal, setShowViewInventoryModal] = useState(false);

  const [installationOrder, setInstallationOrder] = useState<any>();
  const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
  const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [installationCost, setInstallationCost] = useState<number | null>(null);
  const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [openProductStatusIndex, setOpenProductStatusIndex] = useState<number | null>(null);
  const [rotationalPoolProducts, setRotationalPoolProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any | null>(null);
  const [availableSerializedUnits, setAvailableSerializedUnits] = useState<any[]>([]);
  const [allocatedUnits, setAllocatedUnits] = useState<{
    [linkedProductId: string]: {
      rotationalProductId: string;
      warehouseId: string;
      serial_id: string;
      serial_number: string;
      product_name: string;
      sku: string;
      storage_location: {
        warehouse_id: string;
        warehouse: string;
        zone_id: string;
        zone: string;
        rack_id: string;
        rack: string;
        bin_id: string;
        bin: string;
      };
    }[];
  }>({});

  const [linkedProductIdContext, setLinkedProductIdContext] = useState<string | null>(null);

  const navigate = useNavigate();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<Selected | null>(null);

  const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);

  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);

  const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);

  const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Address | null>(null);
  const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
    useState<Address | null>(null);

  const [installationAddress, setInstallationAddress] = useState<Address>();

  const buyerOptions: { value: string; label: string }[] = [
    { value: "brokerageFirm", label: "Brokerage Firm" },
    { value: "agent", label: "Agent" },
  ];

  const brokerageOptions = brokerageFirms?.map((office) => ({
    value: office.id,
    label: office?.firmDetails?.companyName + "(" + office?.firstName + ")",
    firstName: office?.firstName,
    lastName: office?.lastName,
    email: office?.email,
    phone: office?.phone1,
  }));

  const teamMemberOptions = teamMembers?.map((member) => ({
    value: member.id,
    label: `${member.firstName} ${member.lastName}`,
    email: member.email,
  }));

  const officeOptions = offices?.map((office) => ({
    value: office?._id,
    label: office?.officeName,
    firstName: office?.officeName,
    lastName: office?.officeName,
    email: "",
    phone: office?.officeAddress?.[0]?.phone,
  }));

  const officeAgentOptions = officeAgents?.map((agent) => ({
    value: agent.id,
    label: agent.firstName + " " + agent.lastName,
    firstName: agent.firstName,
    lastName: agent.lastName,
    email: agent.email,
    phone: agent.phone1,
  }));

  const agentOptions = agents?.map((agent) => ({
    value: agent.id,
    label: agent.firstName + " " + agent.lastName,
    firstName: agent.firstName,
    lastName: agent.lastName,
    email: agent.email,
    phone: agent.phone1,
  }));

  const productOptions = productObjs?.map((item) => ({
    value: item.product_id,
    label: item.product.name,
  }));

  const priorityOptions = [
    { value: 1, label: "High" },
    { value: 2, label: "Medium" },
    { value: 3, label: "Low" },
  ];

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        console.log("Installation Orders:", data);
        if (!response.ok) throw new Error(data.message || "Failed to fetch order details");

        setInstallationOrder(data);
      } catch (err: any) {
        console.error("Order Detail Fetch Error:", err.message);
      }
    };

    fetchOrderDetails();
  }, [installationId, token]);

  useEffect(() => {
    if (installationOrder) {
      const addr = installationOrder?.installation_details?.installation_address;
      setInstallationAddress(addr);
    }
  }, [installationOrder]);

  const fetchBrokerageFirms = async (userType: string) => {
    formik.setFieldValue("userType", userType);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/brokerage-firms`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      console.log("fetchBrokerageFirms data :", data);

      if (!response.ok) {
        // throw new Error("Failed to fetch agents");
        showError(data.error || "Failed to fetch firms");
      }

      setBrokerageFirms(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const featchBrokerageTeammembers = async (brokerage: any) => {
    setSelectedBrokerageFirm(brokerage);
    console.log("selectedBrokarageFirms:", brokerage);
    let brokerageId = brokerage?.value;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${brokerageId}&licenseType=BROKERAGE_TEAM_MEMBER`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      console.log("featchBrokerageTeammembers :", data);

      if (!response.ok) {
        showError(data.error || "Failed to fetch offices");
      } else {
        setTeamMembers(data);
        const normalizedOffices = data?.flatMap((member: any) =>
          (member.offices || [])?.map((office: any) => ({
            _id: office._id,
            officeName: office.officeName,
            officeType: office.officeType,
            officeAddress: [office.officeAddress], // ✅ wrap in array to match expected type
          }))
        );

        setOffices(normalizedOffices);
        // ✅ Set selectedOffice based on installationOrder
        if (installationOrder?.office?._id && normalizedOffices.length > 0) {
          const matchedOffice = normalizedOffices.find(
            (office: any) => office._id === installationOrder.office._id
          );
          if (matchedOffice) {
            const officeOption = {
              value: matchedOffice._id,
              label: matchedOffice.officeName,
              firstName: matchedOffice.officeName,
              lastName: matchedOffice.officeName,
              email: "",
              phone: matchedOffice.officeAddress?.[0]?.phone || "",
            };
            setSelectedOffice(officeOption);
            fetchLinkedProducts("office", officeOption.value);
            fetchOfficeAgents(officeOption);
          }
        }


        // console.log("offices :", normalizedOffices);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };


  const fetchOfficeAgents = async (office: any) => {
    setSelectedOffice(office);
    console.log("selectedOffices In fetchOfficeAgents:", office);
    let officeId = office?.value;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/office/${officeId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      console.log("officeAgents :", data);

      if (!response.ok) {
        showError(data.error || "Failed to fetch agents");
      } else {
        setOfficeAgents(data);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/signage/${userId}/agents`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch agents");
        }

        const data: Agent[] = await response.json();
        console.log("signage agents :", data);
        setAgents(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [token]);

  const fetchUserAddresses = async (user: any) => {
    // console.log("fetchUserAddresses IN")
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      console.log("user addresses :", data);
      // setAddresses(data.addresses);
      setAddressObjects(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchOfficeAddresses = async (office: any) => {
    // console.log("fetchOfficeAddresses IN")
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/offices/${office?.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      console.log("office addresses :", data.officeAddress);
      setAddressObjects(data.officeAddress);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (installationOrder?.buyer_type === "brokerageFirm") {
      console.log("brokerageFirm IN")
      console.log("selectedOffice:", selectedOffice);
      if (selectedOfficeAgent) {
        fetchUserAddresses(selectedOfficeAgent);
      } else if (selectedOffice) {
        fetchOfficeAddresses(selectedOffice);
      }
    } else if (formik.values.userType === "agent") {
      console.log("Agents IN")
      if (selectedAgent) {
        fetchUserAddresses(selectedAgent);
      }
    }
  }, [
    installationOrder?.buyer_type,
    selectedAgent,
    selectedOfficeAgent,
    selectedOffice,
    token,
  ]);

  useEffect(() => {
    if (selectedOfficeAgent) {
      fetchUserAddresses(selectedOfficeAgent);
    } else if (selectedAgent) {
      fetchUserAddresses(selectedAgent);
    } else if (selectedOffice) {
      fetchOfficeAddresses(selectedOffice);
    }
  }, [selectedAgent, selectedOfficeAgent, selectedOffice, token]);

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        ...address,
        value: address.address_details ? address.address_details._id : address._id,
        label: address.label,
      }))
      : [];

  const fetchLinkedProducts = async (
    userType: "agent" | "office",
    user_Id: string,
  ) => {
    console.log("fetchLinkedProducts IN");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/inventory/${userType}/${user_Id}/signage/${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      console.log("Linked Products:", data);
      setProductObjs(data);

      if (!response.ok) {
        showError(data?.error || "Failed to fetch linked products");
      } else {
        // setLinkedProducts(data); if needed
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    }
  };

  // we are fetching signage team members for showing Assigned To dropdown
  useEffect(() => {
    const featchSignageTeammembers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${userId}&licenseType=SIGNAGE_TEAM_MEMBER`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        console.log("featchSignageTeammembers:", data);

        const installers = data?.map((user: any) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone1,
        }));

        setInstaller(installers); // 🟢 Now it's filtered and formatted
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    featchSignageTeammembers();
  }, [token, userId]);


  // based on instaletion Address we are calling the dyniamc cost
  useEffect(() => {
    const fetchInstallationCost = async () => {
      if (!installationAddress?.latitude || !installationAddress?.longitude) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              installation_address: {
                addressLine1: installationAddress.addressLine1,
                addressLine2: installationAddress.addressLine2,
                city: installationAddress.city,
                state: installationAddress.state,
                country: installationAddress.country,
                zip: installationAddress.zip,
                cityCode: installationAddress.cityCode,
                stateCode: installationAddress.stateCode,
                countryCode: installationAddress.countryCode,
                latitude: installationAddress.latitude,
                longitude: installationAddress.longitude,
              }
            }),
          }
        );

        const data = await response.json();
        console.log("Installation cost:", data);
        formik.setFieldValue('installation_charge', data.cost || 0);
        if (!response.ok) throw new Error(data?.error || "Failed to fetch cost");

        setInstallationCost(data.cost || 0); // adjust based on response shape
      } catch (error: any) {
        console.error("Cost fetch error:", error.message);
        setInstallationCost(null);
      }
    };

    fetchInstallationCost();
  }, [installationAddress, token]);

  // ===== Types =====
  type Status = "on_site" | "with_supplier";

  type LinkedInstallerLocation = {
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  };

  type LinkedInstallerRow = {
    current_status: Status;                 // "on_site" | "with_supplier"
    product_location_status: string;        // mirror to result rows
    quantity?: number;                      // for bucket rows
    location?: LinkedInstallerLocation[];   // for per-location rows
  };

  type LinkedQuantityLocation = {
    quantity: number;
    current_status: Status;                 // EXACTLY "on_site" or "with_supplier"
    product_location_status: string;
    location?: Array<LinkedInstallerLocation & { quantity: number }>;
  };

  // ===== Normalizers & helpers =====
  function normalizeStatus(s: any): Status {
    const k = String(s || "").trim().toLowerCase();
    if (k === "on_site" || k === "on site" || k === "onsite") return "on_site";
    if (k === "with_supplier" || k === "with supplier" || k === "withsupplier") return "with_supplier";
    return "with_supplier";
  }

  function getLiRowsFromMatched(matched: any): LinkedInstallerRow[] {
    const rows: any[] = matched?.linked_installer_details?.storage_locations ?? [];
    return rows.map((r) => {
      const status = normalizeStatus(r.current_status);
      const hasLocs = Array.isArray(r.location) && r.location.length > 0;
      return {
        current_status: status,
        product_location_status: String(r.product_location_status || ""),
        quantity: hasLocs ? undefined : Number(r.quantity) || 0,
        location: hasLocs
          ? r.location.map((loc: any) => ({
            quantity: Number(loc.quantity) || 0,
            warehouse_id: String(loc.warehouse_id || ""),
            warehouse_name: String(loc.warehouse_name || ""),
            zone_id: String(loc.zone_id || ""),
            zone_name: String(loc.zone_name || ""),
            rack_id: String(loc.rack_id || ""),
            rack_name: String(loc.rack_name || ""),
            bin_id: String(loc.bin_id || ""),
            bin_name: String(loc.bin_name || ""),
          }))
          : [],
      };
    });
  }

  const sumAll = (rows: LinkedInstallerRow[]) =>
    rows.reduce((t, r) => {
      if (r.location?.length) {
        return t + r.location.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      }
      return t + (Number(r.quantity) || 0);
    }, 0);

  const sumStorage = (items: LinkedQuantityLocation[] = []) =>
    items.reduce((t, r) => t + (Number(r.quantity) || 0), 0);

  // ===== STRICT allocator =====
  // - on_site first
  // - with_supplier bucket rows wrapped into location[0] with blank IDs
  // - with_supplier per-location combined into ONE entry per row (location[] holds all splits)
  function allocateFromLinkedInstallerStrict(
    requestedQty: number,
    liRows: LinkedInstallerRow[],
  ): LinkedQuantityLocation[] {
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
      throw new Error(`Invalid requestedQty: ${requestedQty}`);
    }
    if (!Array.isArray(liRows)) throw new Error("linked_installer rows invalid");

    const liTotal = sumAll(liRows);
    if (liTotal < requestedQty) {
      throw new Error(
        `Insufficient linked_installer stock (${liTotal}) for requested quantity (${requestedQty})`
      );
    }

    let remaining = requestedQty;
    const result: LinkedQuantityLocation[] = [];

    // A) ON_SITE buckets
    const rowsOnSiteBuckets = liRows.filter(
      (r) => r.current_status === "on_site" && !(r.location?.length)
    );
    for (const row of rowsOnSiteBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "on_site",
          product_location_status: row.product_location_status,
        });
        remaining -= take;
      }
    }

    // B1) WITH_SUPPLIER bucket rows (wrap into single location item)
    const rowsSupplierBuckets = liRows.filter(
      (r) => r.current_status === "with_supplier" && !(r.location?.length)
    );
    for (const row of rowsSupplierBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: [
            {
              quantity: take,
              warehouse_id: "",
              warehouse_name: "",
              zone_id: "",
              zone_name: "",
              rack_id: "",
              rack_name: "",
              bin_id: "",
              bin_name: "",
            },
          ],
        });
        remaining -= take;
      }
    }

    // B2) WITH_SUPPLIER per-location rows (combine into one entry per row)
    const rowsSupplierPerLoc = liRows.filter(
      (r) => r.current_status === "with_supplier" && (r.location?.length ?? 0) > 0
    );
    for (const row of rowsSupplierPerLoc) {
      if (remaining === 0) break;

      const locArray: LinkedInstallerLocation[] = [];
      for (const loc of row.location || []) {
        if (remaining === 0) break;
        const take = Math.min(remaining, Number(loc.quantity) || 0);
        if (take > 0) {
          locArray.push({
            quantity: take,
            warehouse_id: loc.warehouse_id,
            warehouse_name: loc.warehouse_name,
            zone_id: loc.zone_id,
            zone_name: loc.zone_name,
            rack_id: loc.rack_id,
            rack_name: loc.rack_name,
            bin_id: loc.bin_id,
            bin_name: loc.bin_name,
          });
          remaining -= take;
        }
      }

      if (locArray.length > 0) {
        result.push({
          quantity: locArray.reduce((sum, l) => sum + l.quantity, 0),
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: locArray,
        });
      }
    }

    if (remaining > 0) throw new Error("insufficient stock after allocation");

    const sum = result.reduce((s, r) => s + r.quantity, 0);
    if (sum !== requestedQty) throw new Error(`Allocation sum (${sum}) != requested quantity (${requestedQty})`);

    // enforce on_site-first invariant if any on_site supply exists
    const onSiteAvail = rowsOnSiteBuckets.reduce((n, r) => n + (Number(r.quantity) || 0), 0);
    if (onSiteAvail > 0) {
      const expectedOnSite = Math.min(onSiteAvail, requestedQty);
      const allocatedOnSite = result.filter(r => r.current_status === "on_site")
        .reduce((n, r) => n + r.quantity, 0);
      if (allocatedOnSite < expectedOnSite) {
        throw new Error(`On-site-first violated; expected ${expectedOnSite}, got ${allocatedOnSite}`);
      }
    }

    return result;
  }

  // ===== Merge & reconcile for EDIT =====
  function mergeSupplierEntries(items: LinkedQuantityLocation[]): LinkedQuantityLocation[] {
    const onSite = items.filter(i => i.current_status === "on_site");
    const supplier = items.filter(i => i.current_status === "with_supplier");
    if (supplier.length <= 1) return items;

    // If product_location_status differs, pick the first (or add your policy)
    const pls = supplier[0]?.product_location_status || "";
    const combinedLoc = supplier.flatMap(s => s.location ?? []);
    const merged: LinkedQuantityLocation = {
      quantity: combinedLoc.reduce((n, l) => n + (Number(l.quantity) || 0), 0),
      current_status: "with_supplier",
      product_location_status: pls,
      location: combinedLoc,
    };
    return [...onSite, merged];
  }

  function trimStorageDown(oldStorage: LinkedQuantityLocation[], toRemove: number): LinkedQuantityLocation[] {
    let remaining = toRemove;
    const copy = JSON.parse(JSON.stringify(oldStorage)) as LinkedQuantityLocation[];

    // Trim supplier per-location first
    const sIdx = copy.findIndex(s => s.current_status === "with_supplier");
    if (sIdx >= 0 && remaining > 0) {
      const s = copy[sIdx];
      if (Array.isArray(s.location) && s.location.length > 0) {
        for (let i = s.location.length - 1; i >= 0 && remaining > 0; i--) {
          const loc = s.location[i];
          const take = Math.min(remaining, Number(loc.quantity) || 0);
          loc.quantity -= take;
          remaining -= take;
          if (loc.quantity <= 0) s.location.splice(i, 1);
        }
        s.quantity = (s.location ?? []).reduce((n, l) => n + (Number(l.quantity) || 0), 0);
        if ((s.location?.length ?? 0) === 0) copy.splice(sIdx, 1);
      }
    }

    // Trim on_site last
    if (remaining > 0) {
      const onSite = copy.find(s => s.current_status === "on_site");
      if (onSite) {
        const take = Math.min(remaining, Number(onSite.quantity) || 0);
        onSite.quantity -= take;
        remaining -= take;
        if (onSite.quantity <= 0) {
          const idx = copy.indexOf(onSite);
          copy.splice(idx, 1);
        }
      }
    }

    if (remaining > 0) throw new Error(`Cannot trim more than allocated; leftover ${remaining}`);
    return copy;
  }

  function reconcileStorageForEdit(
    originalStorage: LinkedQuantityLocation[] | undefined,
    requestedQty: number,
    liRows: LinkedInstallerRow[]
  ): LinkedQuantityLocation[] {
    const old = originalStorage ?? [];
    const oldQty = sumStorage(old);

    if (requestedQty === oldQty) {
      return old; // keep exact same breakdown
    }

    if (requestedQty > oldQty) {
      const delta = requestedQty - oldQty;
      const extra = allocateFromLinkedInstallerStrict(delta, liRows);
      return mergeSupplierEntries([...old, ...extra]);
    }

    // requested < old
    const trimmed = trimStorageDown(old, oldQty - requestedQty);
    const sum = sumStorage(trimmed);
    if (sum !== requestedQty) throw new Error(`After trim, sum ${sum} != requested ${requestedQty}`);
    return mergeSupplierEntries(trimmed);
  }


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userType: installationOrder?.user_type,
      userFirstName: "",
      userLastName: "",
      userEmail: installationOrder?.user_email,
      userPhone: installationOrder?.user_phone,
      brokerageFirmsId: installationOrder?.brokerage_firm_id,
      teamMemberId: installationOrder?.team_member_id,
      company: "",
      office: installationOrder?.office,
      agent: installationOrder?.agent_id,
      scheduled_date: installationOrder?.installation_details?.scheduled_date?.split("T")[0] || "",
      jobs_today: 0,
      installeId: installationOrder?.installation_details.installer_id,
      priority: installationOrder?.priority,
      billing_address: installationOrder?.billing_address,
      installation_address: installationOrder?.installation_details.installation_address,
      isSameAsBilling: !!installationOrder?.both_address_same,
      billingAddressId: installationOrder?.billing_address._id,
      installationAddressId: installationOrder?.installation_details.installation_address.label || "",
      location: "",
      products: "",
      instructions: "",
      payment_method: "",
      image: null,
      installation_charge: installationOrder?.installation_cost,
      discount: installationOrder?.discount_amount,
      subtotal: installationOrder?.subtotal_amount,
      total: installationOrder?.total_amount,
      tax: 0,
      localLawsChecked: installationOrder?.local_laws_checked,
    },
    validationSchema: Yup.object({
      userEmail: Yup.string()
        .email("Enter a valid email")
        .required("Email is required"),
      userPhone: Yup.string().required("Phone number is required"),

      // ✅ Phone required (basic pattern check optional)
      // userPhone: Yup.string()
      //   .required("Phone number is required")
      //   .matches(/^[0-9]{10}$/, "Enter a valid phone number"),

      // ✅ Billing Address required
      billingAddressId: Yup.string().required("Billing address is required"),

      // ✅ Installation address required only if isSameAsBilling is false
      installationAddressId: Yup.string().when("isSameAsBilling", {
        is: false,
        then: (schema) => schema.required("Installation address is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // ✅ Checkbox (just a boolean, no error)
      isSameAsBilling: Yup.boolean(),

      // ✅ Scheduled date required
      scheduled_date: Yup.string().required("Scheduled date is required"),

      // ✅ Jobs Today - optional (disabled field)
      jobs_today: Yup.string().notRequired(),

      // ✅ Installer required
      installeId: Yup.string().required("Installer is required"),

      // ✅ Priority required
      priority: Yup.string().required("Priority is required"),
      // installer: Yup.string().required("Installer is required"),
      // installation_date: Yup.string().required("Installation date is required"),
      // billing_address: Yup.string().required("Billing address is required"),
      // installation_address: Yup.string().required(
      //   "Installation address is required"
      // ),
      // image: Yup.mixed().required("Image is required"),
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      // Example file upload handling
      const imageUrl = values.image ? await uploadFile(values.image) : null;

      let officeInfo = null;
      let agentId = null;
      let sourceId = null;
      let agentName = null
      let customerId = null;
      let inventorySource = "agent"; // default fallback
      let finalBillingAddress = null;
      let finalInstallationAddress = null;
      if (selectedOfficeAgent) {
        agentId = selectedOfficeAgent?.value;
        sourceId = selectedOfficeAgent?.value
        agentName = selectedOfficeAgent?.label
        customerId = selectedOfficeAgent?.value;
        inventorySource = "agent";
        const selectedOfficeData = offices?.find(
          (office) => office._id === selectedOffice?.value
        );
        const primaryAddress = selectedOfficeData?.officeAddress?.[0];
        officeInfo = {
          _id: selectedOffice?.value,
          officeName: selectedOffice?.label,
          officeType: selectedOffice?.label,
          officeAddress: primaryAddress,
          status: true,
        };
        finalBillingAddress = selectedBillingAddress;
        finalInstallationAddress = installationAddress;
      } else if (selectedAgent) {
        agentId = selectedAgent?.value;
        sourceId = selectedAgent?.value;
        agentName = selectedAgent?.label
        customerId = selectedAgent?.value;
        inventorySource = "agent";
        officeInfo = null;
        finalBillingAddress = selectedBillingAddress;
        finalInstallationAddress = installationAddress;
      } else if (selectedOffice) {
        const selectedOfficeData = offices?.find(
          (office) => office._id === selectedOffice?.value
        );
        const primaryAddress = selectedOfficeData?.officeAddress?.[0];
        officeInfo = {
          _id: selectedOffice?.value,
          officeName: selectedOffice?.label,
          officeType: selectedOffice?.label,
          officeAddress: primaryAddress,
          status: true,
        };
        sourceId = selectedOffice?.value;
        inventorySource = "office";
        agentId = null;
        agentName = null
        finalBillingAddress = selectedOfficeBillingAddress;
        finalInstallationAddress = installationAddress;
      }

      let lineItems: any[] = [];

      // 📦 1. Regular products (agent or office)
      console.log("selectedProducts:", selectedProducts)
      lineItems = selectedProducts?.filter((product) => product.inventory_type !== "rotational")?.map((product) => {
        const matched = productObjs.find((p) => p.product_id === product.product_id);
        const variant: any = matched?.product?.variants?.[0] || {};
        const allocated = allocatedUnits[product.product_id] || [];

        // 🧠 Check if quantity changed from original order
        const originalItem = installationOrder?.line_items?.find(
          (item: any) => item.product_id === product.product_id
        );
        const originalQty = Number(originalItem?.quantity || 0);
        const currentQty = Number(product.quantity || 0);
        const quantityUnchanged = originalQty === currentQty

        // Build storage_locations only for agent/office
        const invType = (product?.inventory_type || matched?.inventory_type || "").toLowerCase();
        let storage_locations: LinkedQuantityLocation[] = [];

        if (invType === "agent" || invType === "office") {
          const liRows = getLiRowsFromMatched(matched);
          storage_locations = reconcileStorageForEdit(
            originalItem?.storage_locations,
            currentQty,
            liRows
          );
        } else {
          storage_locations = [];
        }

        return {
          product_id: product.product_id,
          variant_id: product.variant_id || variant?.variant_id,
          inventory_source: product?.inventory_type || "", // agent or office
          warehouse_id: null,
          serial_ids: [],
          product_name: product?.name || "",
          sku: product?.sku || "",
          quantity: Number(product.quantity),
          unit_price: Number(product?.retail_price || 0),
          total_price: Number(product?.retail_price || 0) * Number(product.quantity),
          discount: 0,
          tax_amount: 0,
          inventory_deducted: false,
          fulfillment_type: "stock",
          production_status: product.productionStatus || "not started",
          is_rotational: product?.is_rotational || false,
          current_status: product?.product_location_status || "",
          notes: product.notes || "",
          rotational_allocated: quantityUnchanged ? allocated.length : 0, // ✅ here’s the fix
          // storage_locations:
          //   product.storage_locations && product.storage_locations.length > 0
          //     ? product.storage_locations?.map((loc: any) => ({
          //       ...loc,
          //       quantity: quantityUnchanged ? loc.quantity : Number(product.quantity)
          //     }))
          //     : [
          //       {
          //         quantity: Number(product.quantity),
          //         product_location_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
          //         current_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
          //         assigned_at: new Date().toISOString(),
          //         location: matched?.linked_installer_details?.storage_locations?.[0]?.location || {},
          //       }
          //     ],
          storage_locations,
        };
      });

      // 🔄 2. Rotational allocations (only push if linked product quantity hasn't changed)
      Object.entries(allocatedUnits).forEach(([linkedProductId, units]) => {
        console.log("units:", units);
        if (units.length === 0) return;

        console.log("linkedProductId:", linkedProductId);
        console.log("units:", units);

        // Get the original line item for this linked product
        const originalItem = installationOrder?.line_items?.find(
          (item: any) => item.product_id === linkedProductId
        );

        // Get the current (edited) selected product
        const updatedProduct = selectedProducts.find(
          (p) => p.product_id === linkedProductId
        );

        console.log("originalItem :", originalItem);
        console.log("updatedProduct :", updatedProduct)

        if (!originalItem || !updatedProduct) return;

        const originalQty = Number(originalItem.quantity || 0);
        const currentQty = Number(updatedProduct.quantity || 0);

        console.log("originalQty:", originalQty, "currentQty:", currentQty);

        // ❌ Skip if quantities are different
        if (originalQty !== currentQty) return;

        // ✅ Push rotational product allocation
        lineItems.push({
          source_product_id: linkedProductId,
          product_id: units[0].rotationalProductId,
          inventory_source: "rotational",
          warehouse_id: units[0].warehouseId || "",
          // serial_ids: units.map((u) => u.serial_id),
          // serial_codes: units.map((u) => u.serial_number),
          product_name: units[0].product_name || "",
          sku: units[0].sku || "",
          quantity: 1,
          unit_price: 0,
          total_price: 0,
          discount: 0,
          tax_amount: 0,
          inventory_deducted: false,
          fulfillment_type: "stock",
          production_status: null,
          is_rotational: true,
          current_status: "",
          notes: "",
          serial_allocations: units?.map((u: any) => ({
            source_product_id: linkedProductId,
            serial_id: u.serial_id,
            serial_code: u.serial_number,
            warehouse_id: u.storage_location?.warehouse_id || "",
            zone_id: u.storage_location?.zone_id || "",
            rack_id: u.storage_location?.rack_id || "",
            bin_id: u.storage_location?.bin_id || "",
            status: u.storage_location?.status || "",
          })),
        });
      });
      console.log("lineItems:", lineItems)
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders/update/${installationId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            // order_number: installationOrder?.order_number,
            // inventory_source: inventorySource,
            // source_id: sourceId,
            // brokerage_firm_id: selectedBrokerageFirm?.value,
            // brokerage_firm_name: selectedBrokerageFirm?.label,
            // team_member_id: values.teamMemberId,
            // office: officeInfo,
            // agent_id: agentId,
            // agent_name: agentName,
            // signage_id: userId,
            // signage_company_name: userName,
            line_items: lineItems,
            // customer_group_id: null,
            // payment_method: values.payment_method,
            installation_details: {
              scheduled_date: new Date(values.scheduled_date).toISOString(),
              installation_address: finalInstallationAddress,
              installer_id: selectedInstaller?.value,
              installer_name: selectedInstaller?.label,
              contact_number: selectedInstaller?.phone,
              estimated_duration: null,
              actual_installation_date: null,
              additional_instructions: [
                {
                  value: values.instructions,
                  added_by: {
                    id: userId,
                    name: userName,
                  },
                  added_at: new Date().toISOString(),
                },
              ],
              assignee_id: selectedInstaller?.value,
              tax_details: null,
            },
            billing_address: finalBillingAddress,
            additional_instructions: values.instructions,
            order_type: "installation",
            // local_laws_compliance: true,
            priority: values.priority,
            uploaded_files: uploadedFileMeta || [],
            subtotal_amount: values.subtotal,
            installation_cost: parseFloat(String(installationCost)) || 0,
            additional_cost: 0,
            // tax_amount: 0,
            discount_amount: values.discount,
            total_amount: values.total,
            // overpaid_amount: 0,
            version: installationOrder?.version,
            // tax_inclusive: false,
            // tax_details: null,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          showError(data.message || data.error || "Failed to update installation order");
          throw new Error(data.message || "Failed to create installation order");
        }

        showSuccess("Installation order created successfully!");
        navigate("/signage/jobs/installations");
      } catch (error: any) {
        showError(`Error: ${error.message}`);
      } finally {
        setFormSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (formik.values.installeId && Array.isArray(installer) && installer.length > 0) {
      const matched = installer.find(i => i.value === formik.values.installeId);
      if (matched) setSelectedInstaller(matched);
    }
  }, [formik.values.installeId, installer]);

  // useEffect(() => {
  //   if (formik.values.installeId && installer.length > 0) {
  //     const matched = installer.find(i => i.value === formik.values.installeId);
  //     if (matched) setSelectedInstaller(matched);
  //   }
  // }, [formik.values.installeId, installer]);

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "installation_orders");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.path);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  const handleLocationChange = (
    selectedOption: SingleValue<{ value: string; label: string }>
  ) => {
    formik.setFieldValue("location", selectedOption?.value || "");
  };

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setSelectedLocation({ lat, lng });
      formik.setFieldValue("location", { lat, lng });
    }
  }, []);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log("Place changed:", place);

      const addressComponents = place.address_components;
      console.log(addressComponents);

      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();

        console.log("Latitude:", latitude, "Longitude:", longitude);

        // Update form values
        // formik.setFieldValue('streetAddress1', street);
        // formik.setFieldValue('city', cityObj.long_name);
        // formik.setFieldValue('county', cityObj.long_name);
        // formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        // formik.setFieldValue('state', stateObj.long_name);
        // formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        // formik.setFieldValue('zipcode', postalCode);
        // formik.setFieldValue('country', countryObj.long_name);
        // formik.setFieldValue('countryCode', countryObj.short_name); 

        // if (latitude && longitude) {
        //   formik.setFieldValue('latitude', latitude);
        //   formik.setFieldValue('longitude', longitude);
        // }

        const installation_address = street + ',' + cityObj.long_name + ', ' + stateObj.long_name + ', ' + postalCode;
        setInstallationAddress({
          _id: '',
          label: installation_address,
          addressLine1: street,
          addressLine2: '',
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: latitude || 0,
          longitude: longitude || 0,
          phone: '',
        });

        formik.setFieldValue('installationAddressId', installation_address);
      }
    }
  };

  // to get the scheduled job count based on the selected date
  useEffect(() => {
    const fetchScheduledJobs = async () => {
      if (!formik.values.scheduled_date || !userId) return;

      const date = new Date(formik.values.scheduled_date).toISOString().split("T")[0]; // format as YYYY-MM-DD

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.installeId}/${date}/scheduled_jobs`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to fetch job count");
        console.log("Scheduled Jobs:", data);

        setScheduledJobsCount(data.scheduled_jobs); // assuming response shape: { count: number }
        formik.setFieldValue("jobs_today", data.scheduled_jobs);
      } catch (err: any) {
        console.error("Scheduled Jobs Error:", err.message);
        setScheduledJobsCount(null);
      }
    };

    fetchScheduledJobs();
  }, [formik.values.installeId, userId, token]);

  // seting the total units based on the selected products
  useEffect(() => {
    const newTotal = selectedProducts
      .filter((p) => p.inventory_type !== "rotational")
      .reduce((sum, product) => sum + (product.quantity || 0), 0);

    setTotalUnits(newTotal);
  }, [selectedProducts]);


  // seting the installation address based on the selected billing address
  useEffect(() => {
    if (formik.values.isSameAsBilling) {
      const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
      const billing = isOffice ? selectedOfficeBillingAddress : selectedBillingAddress;

      if (billing) {
        console.log("Billing Address:", billing);
        setInstallationAddress(billing);
        formik.setFieldValue("installationAddressId", billing.label || "");
      }
    }
  }, [formik.values.isSameAsBilling, selectedBillingAddress, selectedOfficeBillingAddress]);

  // calculating the subtotal and total
  useEffect(() => {
    const subtotal = Number(installationCost) + Number(formik.values.tax);
    const total = subtotal - Number(formik.values.discount || 0);

    formik.setFieldValue("subtotal", subtotal);
    formik.setFieldValue("total", total);
  }, [installationCost, formik.values.tax, formik.values.discount]);

  // remove the preview images
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  useEffect(() => {
    if (
      installationAddress?.latitude &&
      installationAddress?.longitude &&
      installationAddress.latitude !== 0 &&
      installationAddress.longitude !== 0
    ) {
      setSelectedLocation({
        lat: installationAddress.latitude,
        lng: installationAddress.longitude,
      });
    }
  }, [installationAddress]);


  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!lat || !lng) return;

    setSelectedLocation({ lat, lng });

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results[0]) {
        const place = response.results[0];
        const addressComponents = place.address_components;

        const getComponent = (type: string) => {
          const component = addressComponents.find((c: any) =>
            c.types.includes(type)
          );
          return component
            ? { long_name: component.long_name, short_name: component.short_name }
            : { long_name: "", short_name: "" };
        };

        const streetNumber = getComponent("street_number").long_name;
        const route = getComponent("route").long_name;
        const cityObj =
          getComponent("locality") ||
          getComponent("administrative_area_level_2");
        const stateObj = getComponent("administrative_area_level_1");
        const postalCode = getComponent("postal_code").long_name;
        const countryObj = getComponent("country");

        const street = `${streetNumber} ${route}`.trim();

        const newAddress = {
          _id: "",
          label: place.formatted_address,
          addressLine1: street,
          addressLine2: "",
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: lat,
          longitude: lng,
          phone: "",
        };

        setInstallationAddress(newAddress);
        formik.setFieldValue("installationAddressId", place.formatted_address);
      }
    } catch (error) {
      console.error("Geocoder failed", error);
      showError("Unable to update address from marker position.");
    }
  };

  // geting the defult selectedAgent and installetion Addreess and getting the brokarageForms
  // useEffect(() => {
  //   if (installationOrder?.agent_id && agents.length > 0) {
  //     const matchedAgent = agents.find(agent => agent.id === installationOrder.agent_id);
  //     if (matchedAgent) {
  //       const option = {
  //         value: matchedAgent.id,
  //         label: `${matchedAgent.firstName} ${matchedAgent.lastName}`,
  //         firstName: matchedAgent.firstName,
  //         lastName: matchedAgent.lastName,
  //         email: matchedAgent.email,
  //         phone: matchedAgent.phone1,
  //       };
  //       setSelectedAgent(option);
  //       fetchLinkedProducts("agent", option.value);
  //     }
  //   }

  //   if (installationOrder?.installation_details?.installation_address) {
  //     setInstallationAddress(installationOrder.installation_details.installation_address);
  //   }

  //   if (installationOrder?.user_type) {
  //     fetchBrokerageFirms(installationOrder.user_type);
  //   }
  // }, [installationOrder, agents]);
  useEffect(() => {
    // ✅ Only run agent check if agents is an array and has items
    if (installationOrder?.agent_id && Array.isArray(agents) && agents.length > 0) {
      const matchedAgent = agents.find(agent => agent.id === installationOrder.agent_id);
      if (matchedAgent) {
        const option = {
          value: matchedAgent.id,
          label: `${matchedAgent.firstName} ${matchedAgent.lastName}`,
          firstName: matchedAgent.firstName,
          lastName: matchedAgent.lastName,
          email: matchedAgent.email,
          phone: matchedAgent.phone1,
        };
        setSelectedAgent(option);
        fetchLinkedProducts("agent", option.value);
      }
    }

    if (installationOrder?.installation_details?.installation_address) {
      setInstallationAddress(installationOrder.installation_details.installation_address);
    }

    if (installationOrder?.user_type) {
      fetchBrokerageFirms(installationOrder.user_type);
    }
  }, [installationOrder, agents]);


  // calling brokarage Teammembers based on selected Brokarage
  useEffect(() => {
    if (
      installationOrder?.brokerage_firm_id &&
      brokerageOptions.length > 0
    ) {
      const selectedFirm = brokerageOptions.find(
        (firm) => firm.value === installationOrder.brokerage_firm_id
      );

      if (
        selectedFirm &&
        selectedBrokerageFirm?.value !== selectedFirm.value
      ) {
        console.log("Selected Brokerage Firm:", selectedFirm);
        // setSelectedBrokerageFirm(selectedFirm);
        featchBrokerageTeammembers(selectedFirm);
      }
    }
  }, [installationOrder?.brokerage_firm_id, brokerageOptions.length]);

  // setting defult selectedProducts
  useEffect(() => {
    if (installationOrder?.line_items?.length > 0) {
      const mapped = installationOrder.line_items?.map((item: any) => ({
        product_id: item.product_id,
        variant_id: item.variant_id || "",
        inventory_type: item.inventory_source || "",
        name: item.product_name || "",
        sku: item.sku || "",
        product_location_status: item.current_status || "",
        user_id: installationOrder?.inventory_source === "office" ? installationOrder?.team_member_id : installationOrder?.agent_id,
        quantity: item.quantity,
        updateStatus: "",
        productionStatus: item.production_status || "",
        is_rotational: item.is_rotational,
        allocatedCount: item.rotational_allocated || 0,
        source_product_id: item.source_product_id || null,
        serial_ids: item.serial_ids || [],
        storage_locations: item.storage_locations || [],
        serial_allocations: item.serial_allocations || [],
        unit_price: item.unit_price || 0,
        total_price: item.total_price || 0,
      }));

      setSelectedProducts(mapped);
      console.log("Mapped Products:", mapped);
    }
  }, [installationOrder]);

  // setting defult allocatedUnits
  useEffect(() => {
    if (!installationOrder?.line_items?.length) return;

    const prefilledAllocations: {
      [linkedProductId: string]: {
        rotationalProductId: string;
        warehouseId: string;
        serial_id: string;
        serial_number: string;
        product_name: string;
        sku: string;
        storage_location: {
          warehouse_id: string;
          warehouse: string;
          zone_id: string;
          zone: string;
          rack_id: string;
          rack: string;
          bin_id: string;
          bin: string;
        };
      }[];
    } = {};

    // for (const item of installationOrder.line_items) {
    //   if (Array.isArray(item.serial_allocations)) {
    //     // console.log("Serial Allocations:", item)
    //     const key = item.inventory_source == "rotational" ? item.source_product_id : item.product_id;
    //     if (!key) continue;
    //     const allocations = item.serial_allocations.map((sid: string, index: number) => ({
    //       rotationalProductId: item.product_id,
    //       warehouseId: item.warehouse_id,
    //       // serial_id: sid,
    //       // serial_number: item.serial_codes?.[index] || sid,
    //       product_name: item.product_name || "Unknown Product",
    //       sku: item.sku || "",
    //       storage_location: item.serial_allocations?.[index] || [],
    //     }));

    //     prefilledAllocations[key] = allocations;
    //   }
    // }
    for (const item of installationOrder.line_items) {
      if (Array.isArray(item.serial_allocations)) {
        for (const alloc of item.serial_allocations) {
          const key = alloc.source_product_id; // <-- Use the true source here!
          if (!key) continue;

          if (!prefilledAllocations[key]) prefilledAllocations[key] = [];

          prefilledAllocations[key].push({
            rotationalProductId: item.product_id,
            warehouseId: alloc.warehouse_id,
            serial_id: alloc.serial_id,
            serial_number: alloc.serial_code,
            product_name: item.product_name || "Unknown Product",
            sku: item.sku || "",
            storage_location: alloc,
          });
        }
      }
    }

    setAllocatedUnits(prefilledAllocations);
    console.log("Allocated Units:", prefilledAllocations);
  }, [installationOrder]);


  useEffect(() => {
    if (!installationOrder || addressOptions.length === 0 || addressObjects.length === 0) return;

    const billingId = formik.values.billingAddressId;
    if (!billingId) return;

    const selectedBilling = addressOptions.find(address => address.value === billingId);
    const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;

    if (selectedBilling) {
      const billingMatch = addressObjects.find(
        (addr) =>
          (addr.address_details ? addr.address_details._id : addr._id) === selectedBilling.value
      );

      if (isOffice) {
        if (!selectedOfficeBillingAddress || selectedOfficeBillingAddress._id !== billingMatch?._id) {
          console.log("Auto-setting Office Billing Address:", billingMatch);
          setSelectedOfficeBillingAddress(billingMatch || null);
        }
      } else {
        const agentBillingDetails = billingMatch?.address_details || null;
        if (!selectedBillingAddress || selectedBillingAddress._id !== agentBillingDetails?._id) {
          console.log("Auto-setting Agent Billing Address:", agentBillingDetails);
          setSelectedBillingAddress(agentBillingDetails);
        }
      }
    }
  }, [
    installationOrder,
    formik.values.billingAddressId,
    addressOptions,
    selectedOfficeAgent,
    selectedAgent,
    selectedOffice,
  ]);

  const updateProductionStatus = async (productId: string, productionStatus: string) => {
    if (!installationId || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/update_production_status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product_id: productId,
            production_status: productionStatus,
          }),
        }
      );

      const data = await response.json();
      console.log("Update Production Status Response:", data);

      if (!response.ok) {
        showError(data?.error || "Failed to update production status");
        return;
      }

      showSuccess("Production status updated successfully!");
    } catch (err: any) {
      showError(err.message || "Error updating production status");
    }
  };

  const isFieldEditable = (status: number, field: string): boolean => {
    const editableMap: Record<number, string[]> = {
      100: [
        // Created (Draft)
        // "BasicDetails",
        "BillingAddress",
        "InstallationAddress",
        "LineItems",
        // "Priority",
        "Instructions",
        "Installer",
        "InstallationDate",
        " LineItemsquantity",
        "clickAddress",
      ],
      200: [
        // Finalized
        "LineItems",
        "InstallationAddress",
        "Instructions",
        "BillingAddress",
        "Installer",
        "LineItemsquantity",
        "clickAddress",
      ],
      300: [
        // Scheduled or Re-scheduled
        // "Priority",
        "InstallationDate", // if ≥ 48h
        "Installer",
        "Instructions",
      ],
      400: [
        // In Progress
        "Instructions",
      ],
      // 500 and 600: No edits allowed
    };

    const nonEditableStatuses = [500, 600]; // Completed, Cancelled

    if (nonEditableStatuses.includes(status)) return false;

    const editableFields = editableMap[status] || [];
    return editableFields.includes(field);
  };

  // const fetchRotationalPoolProducts = async () => {
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     const data = await response.json();
  //     if (!response.ok) throw new Error(data.message || "Failed to fetch");
  //     console.log("Rotational Pool Products:", data);

  //     setRotationalPoolProducts(data); // ✅ store in state
  //   } catch (err: any) {
  //     console.error("Error fetching rotational products:", err.message);
  //     showError("Failed to fetch rotational pool products");
  //   }
  // };

  // const rotationalproductOptions = rotationalPoolProducts.map((p) => ({
  //   value: p._id,
  //   label: p.rotational_product?.name || "Unnamed Product",
  //   fullProduct: p,
  // }));


  // const handleProductSelect = (selected: any) => {
  //   setSelectedProduct(selected);

  //   const warehouses = selected?.fullProduct?.warehouse_details || [];

  //   const options = warehouses.map((w: any) => ({
  //     value: w.warehouse._id,
  //     label: `${w.warehouse.label} (${w.available} available)`,
  //   }));

  //   setWarehouseOptions(options);
  // };

  // const fetchSerializedUnits = async (rotationalId: string, warehouseId: string) => {
  //   if (!userId || !token) return;

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/signage/${userId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     const data = await response.json();
  //     if (!response.ok) throw new Error(data.message || "Failed to fetch serialized pool");
  //     console.log("Serialized Units:", data);
  //     setAvailableSerializedUnits(data);
  //   } catch (err: any) {
  //     console.error("Fetch Serialized Units Error:", err.message);
  //     showError("Unable to fetch serialized units");
  //   }
  // };

  // useEffect(() => {
  //   console.log("AllocatedUnits :", allocatedUnits);
  // }, [allocatedUnits]);


  useEffect(() => {
    if (installationOrder?.uploaded_files) {
      setUploadedFileMeta(installationOrder.uploaded_files);
    }
  }, [installationOrder]);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="page-container">
        <h3 className="m-0 text-center fw-bold ">Edit Installation Order</h3>
      </div>
      <div className="card">
        <div className="card-body">
          {/* <h2>Edit Installation Order</h2> */}
          <form onSubmit={formik.handleSubmit}>
            <div className="row mt-5">
              <div className="col-md-4">
                <label htmlFor="" className="table-cells">Order ID</label>
                <input type="text" name="orderId" id="orderId" className="form-control" value={installationOrder?.order_number} disabled />
              </div>

              <div className="col-md-4 mb-3">
                <label htmlFor="userType" className="form-label table-cells">
                  Buyer Type
                </label>
                <Select
                  // className="form-control"
                  className="br-8"
                  id="userType"
                  name="userType"
                  options={buyerOptions}
                  value={buyerOptions?.find(
                    (option) =>
                      option.value === formik.values.userType
                  )}
                  onChange={(selectedOption) => {
                    formik.setFieldValue("userType", selectedOption?.value)
                    fetchBrokerageFirms(selectedOption?.value || "")
                  }
                  }
                  isDisabled
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.values.userType === "brokerageFirm" ? (
                <>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="brokerageFirmsId" className="table-cells">
                      Select Brokerage Firm
                    </label>
                    <Select
                      // className="form-control"
                      id="brokerageFirmsId"
                      name="brokerageFirmsId"
                      options={brokerageOptions}
                      value={brokerageOptions?.find((option) => option.value === formik.values.brokerageFirmsId) || null}
                      onChange={(selected) => {
                        // setBuyerType(selected?.value || "");
                        featchBrokerageTeammembers(selected || null)
                        formik.setFieldValue("brokerageFirmsId", selected?.value || "");
                      }
                      }
                      isDisabled
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="officeSelection" className="table-cells">
                      Select Team Member
                    </label>
                    <Select
                      id="teamMemberId"
                      name="teamMemberId"
                      // className="form-control"b 
                      options={teamMemberOptions}
                      value={teamMemberOptions?.find((option) => option.value === formik.values.teamMemberId) || null}
                      onChange={(selected) => {
                        formik.setFieldValue("teamMemberId", selected?.value || "");
                        setSelectedTeamMember(selected);
                      }}
                      onBlur={formik.handleBlur}
                      isDisabled
                      placeholder="Select a team member"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="officeSelection" className="table-cells">
                      Select Office
                    </label>
                    <Select
                      // className="form-control"
                      id="officeSelection"
                      options={officeOptions}
                      value={
                        officeOptions?.find(
                          (option) =>
                            option.value === formik.values.office?._id
                        ) || null
                      }
                      onChange={(selected) => {
                        fetchOfficeAgents(selected);
                        console.log("Selected Office:", selected);
                        formik.setFieldValue(
                          "userFirstName",
                          selected?.firstName || ""
                        );
                        formik.setFieldValue(
                          "userLastName",
                          selected?.lastName || ""
                        );
                        formik.setFieldValue(
                          "userEmail",
                          selected?.email || ""
                        );
                        formik.setFieldValue(
                          "userPhone",
                          selected?.phone || ""
                        );
                      }}
                      isDisabled
                    />
                  </div>
                  {officeAgentOptions && (
                    <div className="col-md-4 mb-3">
                      <label htmlFor="officeSelection" className="table-cells">
                        Select Agent
                      </label>
                      <Select
                        // className="form-control"
                        id="officeAgentSelection"
                        options={officeAgentOptions}
                        value={officeAgentOptions?.find((option) => option.value === formik.values.agent) || null}
                        onChange={(selected) => {
                          setSelectedOfficeAgent(selected);
                          // setSelectedAgent(selected);
                          formik.setFieldValue("agent", selected?.value || "")
                          formik.setFieldValue(
                            "userFirstName",
                            selected?.firstName || ""
                          );
                          formik.setFieldValue(
                            "userLastName",
                            selected?.lastName || ""
                          );
                          formik.setFieldValue(
                            "userEmail",
                            selected?.email || ""
                          );
                          formik.setFieldValue(
                            "userPhone",
                            selected?.phone || ""
                          );
                        }}
                        isDisabled
                      />
                    </div>
                  )}
                </>
              ) : (
                formik.values.userType === "agent" && (
                  <div className="col-md-4 mb-3">
                    <label htmlFor="agentSelection" className="table-cells">
                      Select Agent
                    </label>
                    <Select
                      // className="form-control"
                      id="agentSelection"
                      options={agentOptions}
                      value={agentOptions?.find((option) => option.value === formik.values.agent) || null}
                      onChange={(selected) => {
                        setSelectedAgent(selected);
                        formik.setFieldValue("agent", selected?.value || "")
                        formik.setFieldValue(
                          "userFirstName",
                          selected?.firstName || ""
                        );
                        formik.setFieldValue(
                          "userLastName",
                          selected?.lastName || ""
                        );
                        formik.setFieldValue(
                          "userEmail",
                          selected?.email || ""
                        );
                        formik.setFieldValue(
                          "userPhone",
                          selected?.phone || ""
                        );
                      }}
                      onBlur={formik.handleBlur}
                      isDisabled
                    />
                  </div>
                )
              )}

              <div className="col-md-4">
                <label htmlFor="userEmail" className="form-label table-cells">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="userEmail"
                  name="userEmail"
                  placeholder="Enter email"
                  value={formik.values.userEmail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={!isFieldEditable(installationOrder?.status ?? 0, "BasicDetails")}
                />
                {formik.touched.userEmail && typeof formik.errors.userEmail === "string" && (
                  <div className="text-danger">{formik.errors.userEmail}</div>
                )}
              </div>

              {/* ✅ PHONE */}
              <div className="col-md-4">
                <label htmlFor="userPhone" className="form-label table-cells">
                  Phone <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="userPhone"
                  name="userPhone"
                  placeholder="Enter phone"
                  value={formik.values.userPhone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={!isFieldEditable(installationOrder?.status ?? 0, "BasicDetails")}
                />
                {formik.touched.userPhone && typeof formik.errors.userPhone === "string" && (
                  <div className="text-danger">{formik.errors.userPhone}</div>
                )}
              </div>
            </div>
            <hr />
            <h5 className="mt-2 ms-3 table-cells">Address Details </h5>
            <div className="my-2 ms-3">
              <input
                type="checkbox"
                id="isSameAsBilling"
                name="isSameAsBilling"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                checked={formik.values.isSameAsBilling}
                readOnly={!isFieldEditable(installationOrder?.status ?? 0, "clickAddress")}
              />{" "}
              Billing and Installation Address are Same
            </div>

            <div className="row mb-4">
              {/* ✅ BILLING ADDRESS */}
              <div className="col-md-3">
                <div className="mb-3">
                  <label htmlFor="billingAddressId" className="form-label table-cells">
                    Billing Address <span className="text-danger">*</span>
                  </label>
                  <Select
                    id="billingAddress"
                    name="billingAddressId"
                    options={addressOptions}
                    value={
                      addressOptions?.find(
                        (option) => option.value === formik.values.billingAddressId
                      ) || null
                    }
                    onChange={(selected) => {
                      formik.setFieldValue("billingAddressId", selected?.value || "");
                      const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                      const selectedAddress = addressObjects?.find(
                        (addr) =>
                          (addr.address_details
                            ? addr.address_details._id
                            : addr._id) === selected?.value
                      );
                      if (isOffice) {
                        setSelectedOfficeBillingAddress(selectedAddress || null);
                      } else {
                        setSelectedBillingAddress(selectedAddress?.address_details || null);
                      }
                    }}
                    isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "BillingAddress")}
                  />
                  {formik.touched.billingAddressId &&
                    typeof formik.errors.billingAddressId === "string" && (
                      <div className="text-danger">{formik.errors.billingAddressId}</div>
                    )}
                </div>
              </div>

              {/* ✅ INSTALLATION ADDRESS (Only show if checkbox is unchecked) */}
              {formik.values.isSameAsBilling === true ? null : (
                <div className="col-md-4">
                  <div className="mb-3">
                    <label
                      htmlFor="installationAddressId"
                      className="form-label table-cells"
                    >
                      Installation Address <span className="text-danger">*</span>
                    </label>
                    {isLoaded && (
                      <Autocomplete
                        onLoad={(autocomplete) => {
                          autocompleteRef.current = autocomplete;
                        }}
                        onPlaceChanged={handlePlaceChanged}
                      >
                        <input
                          type="text"
                          className="form-control"
                          id="installationAddressId"
                          name="installationAddressId"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.installationAddressId}
                          readOnly={
                            !isFieldEditable(installationOrder?.status ?? 0, "InstallationAddress")
                          }
                        />
                      </Autocomplete>
                    )}
                    {formik.touched.installationAddressId &&
                      typeof formik.errors.installationAddressId === "string" && (
                        <div className="text-danger">
                          {formik.errors.installationAddressId}
                        </div>
                      )}
                  </div>
                </div>
              )}

              <div className="col-md-2">
                <p>&nbsp;</p>
                <i className="ri-search-line fs-3 text-bg-info"></i>
              </div>
            </div>
            <hr />
            <div className="row mb-3">
              {/* Left Column */}
              <div className="col-md-6">
                <div className="row mt-5">
                  <div className="col-md-6 mb-3">
                    <label className="form-label table-cells">
                      Scheduled Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="scheduled_date"
                      name="scheduled_date"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.scheduled_date}
                      readOnly={!isFieldEditable(installationOrder?.status ?? 0, "InstallationDate")}
                    />
                    {formik.touched.scheduled_date &&
                      typeof formik.errors.scheduled_date === "string" && (
                        <div className="invalid-feedback">{formik.errors.scheduled_date}</div>
                      )}
                  </div>

                  {/* ✅ JOBS TODAY */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label table-cells">Jobs Today</label>
                    <input
                      type="text"
                      className="form-control"
                      id="jobs_today"
                      name="jobs_today"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.jobs_today}
                      disabled
                    />
                  </div>

                  {/* ✅ ASSIGNED TO INSTALLER */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label table-cells">
                      Assigned To <span className="text-danger">*</span>
                    </label>
                    <Select
                      id="installeId"
                      name="installeId"
                      options={installer}
                      value={
                        installer?.find(
                          (option) => option.value === formik.values.installeId
                        ) || null
                      }
                      onChange={(selected) => {
                        setSelectedInstaller(selected);
                        formik.setFieldValue("installeId", selected?.value || "");
                      }}
                      onBlur={() => formik.setFieldTouched("installeId", true)}
                      isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "Installer")}
                    />
                    {formik.touched.installeId &&
                      typeof formik.errors.installeId === "string" && (
                        <div className="text-danger">{formik.errors.installeId}</div>
                      )}
                  </div>

                  {/* ✅ PRIORITY */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label table-cells">
                      Priority <span className="text-danger">*</span>
                    </label>
                    <Select
                      id="priority"
                      name="priority"
                      options={priorityOptions}
                      value={
                        priorityOptions.find(
                          (option) => option.value === formik.values.priority
                        ) || null
                      }
                      onChange={(option) => formik.setFieldValue("priority", option?.value || "")}
                      onBlur={() => formik.setFieldTouched("priority", true)}
                      isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "Priority")}
                    />
                    {formik.touched.priority && typeof formik.errors.priority === "string" && (
                      <div className="text-danger">{formik.errors.priority}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <h4 className="table-headers mb-2">Products</h4>
                    <Select
                      id="products"
                      name="products"
                      options={productOptions}
                      value={selectedProductOption}
                      onChange={(selected) => {
                        setSelectedProductOption(selected)
                      }}
                      onBlur={formik.handleBlur}
                      isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "LineItems")}
                    />

                    {formik.touched.products && formik.errors.products && (
                      <div className="text-danger">{formik.errors.products}</div>
                    )}
                  </div>
                  <div className="col-md-6 ">
                    <button
                      className="btn btn-info mt-4"
                      type="button"
                      onClick={() => {
                        if (!selectedProductOption) return;

                        const alreadyExists = selectedProducts.some(
                          (prod) => prod.product_id === selectedProductOption.value
                        );
                        if (alreadyExists) {
                          showError("Product already added");
                          return;
                        }

                        const fullProduct = productObjs.find(
                          (p) => p.product_id === selectedProductOption.value
                        );

                        if (fullProduct) {
                          const flattenedProduct = {
                            product_id: fullProduct.product_id,
                            name: fullProduct.product.name,
                            sku: fullProduct.product.sku || "",
                            inventory_type: fullProduct.inventory_type,
                            product_location_status: fullProduct.product.product_location_status,
                            user_id: fullProduct.agent_id || fullProduct.office_id,
                            quantity: 1,
                            updateStatus: "",
                            productionStatus: "",
                          };

                          setSelectedProducts((prev) => [...prev, flattenedProduct]);
                          setSelectedProductOption(null);
                        }
                        console.log("selectedProducts", selectedProducts);
                      }}
                      disabled={!isFieldEditable(installationOrder?.status ?? 0, "LineItems")}
                    >
                      Add Product
                    </button>

                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h5>Map View</h5>
                <div style={mapContainerStyle}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={10}
                    center={selectedLocation || center}
                  // onClick={handleMapClick}
                  >
                    {selectedLocation && <Marker
                      position={selectedLocation}
                      draggable={true}
                      onDragEnd={handleMarkerDragEnd}
                    />}
                  </GoogleMap>
                </div>
                {formik.touched.location && formik.errors.location && (
                  <div className="text-danger">{formik.errors.location}</div>
                )}
              </div>
            </div>

            <h4 className="text-center mt-4 table-headers">Products in Order</h4>
            <div className="row mb-5">
              <div className="col-md-12">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="text-center table-headers" style={{ width: "17%" }}>Product Name</th>
                      <th className="text-center table-headers" style={{ width: "8%" }}>SKU</th>
                      <th className="text-center table-headers" style={{ width: "8%" }}>Qty</th>
                      <th className="text-center table-headers" style={{ width: "12%" }}>Source</th>
                      <th className="text-center table-headers" style={{ width: "20%" }}>Product Location Current Status</th>
                      <th className="text-center table-headers" style={{ width: "20%" }}>Production Status</th>
                      <th className="text-center table-headers" style={{ width: "10%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts
                      ?.filter((p) => p.inventory_type !== "rotational" && p.inventory_type !== "")?.map((item, index) => {
                        const allocated = allocatedUnits[item.product_id] || [];
                        const remaining = item.quantity - allocated.length;
                        return (
                          <tr key={item.product_id}>
                            <td className="text-center table-cells text-ellipsis">{item.name}</td>
                            <td className="text-center table-cells">{item.sku}</td>
                            <td className="text-center table-cells">
                              <input
                                type="number"
                                className="form-control text-center"
                                value={item.quantity}
                                min={1}
                                onChange={(e) => {
                                  const qty = Number(e.target.value);
                                  const updated = [...selectedProducts];
                                  updated[index].quantity = qty;
                                  setSelectedProducts(updated);
                                }}
                              // readOnly={!isFieldEditable(installationOrder?.status ?? 0, "LineItemsquantity")}
                              />
                            </td>
                            <td className="text-center table-cells">
                              {item.inventory_type === "agent" ? "Agent Inventory" : item.inventory_type === "rotational" ? "Rotational Inventory" : "Office Inventory"}
                            </td>


                            {/* Product Location Current Status & Update */}
                            <td className="text-center table-cells">
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                {/* <span className="bg-warning px-3 py-2" style={{ borderRadius: "10px" }}> */}
                                <StatusBadge status={item.product_location_status?.replace(/_/g, " ") || "N/A"} />
                                {/* {item.product_location_status?.replace(/_/g, " ") || "N/A"} */}
                                {/* </span> */}
                                <Link
                                  className="border-0 p-0"
                                  to={
                                    item.inventory_type === "agent"
                                      ? "/signage/incomingAgentProducts"
                                      : "/signage/incomingOfficeProducts"
                                  }
                                >
                                  <i className="ri-link-m fs-24"></i>
                                </Link>
                              </div>
                            </td>

                            {/* Production Status & Update */}
                            <td className="table-cells text-center align-middle">
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="bg-secondary px-3 py-2 text-white rounded">
                                  {item.productionStatus?.replace(/_/g, " ") || "Not Started"}
                                </span>
                                <button type="button"
                                  className="border-0 p-0"
                                  onClick={() =>
                                    setOpenProductStatusIndex(openProductStatusIndex === index ? null : index)
                                  }
                                >
                                  <i className="border fs-24 ri-arrow-down-s-line rounded-3 shadow"></i>
                                </button>
                              </div>
                              {openProductStatusIndex === index && (
                                <div className="d-flex flex-column align-items-center mt-2 gap-2">
                                  <Select
                                    id={`product_status_${index}`}
                                    name={`product_status_${index}`}
                                    className="w-100"
                                    styles={{ container: (base) => ({ ...base, minWidth: 250 }) }}
                                    options={[
                                      { value: "not_started", label: "Not Started" },
                                      { value: "in_queue", label: "In Queue" },
                                      { value: "in_production", label: "In Production" },
                                      { value: "quality_check", label: "Quality Check" },
                                      { value: "ready_for_pickup", label: "Ready For Pickup" },
                                      { value: "ready_for_installation", label: "Ready for Installation" },
                                    ]}
                                    value={
                                      item.productionStatus
                                        ? { value: item.productionStatus, label: item.productionStatus.replace(/_/g, " ") }
                                        : null
                                    }
                                    onChange={(option) => {
                                      const updated = [...selectedProducts];
                                      updated[index].productionStatus = option?.value || "";
                                      setSelectedProducts(updated);
                                    }}
                                  />

                                  <button
                                    className="btn btn-sm btn-primary rounded-pill px-3 py-2 text-center"
                                    onClick={() => {
                                      const selectedStatus = selectedProducts[index].productionStatus;
                                      const productId = selectedProducts[index].product_id;
                                      updateProductionStatus(productId, selectedStatus);
                                      setOpenProductStatusIndex(null);
                                    }}
                                    disabled={installationOrder.status === 400 || installationOrder.status === 500}
                                  >
                                    Update
                                  </button>
                                </div>
                              )}
                            </td>


                            {/* Actions */}
                            <td className="text-center table-cells">
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm br-8"
                                  onClick={() => {
                                    const removedProductId = item.product_id;

                                    // 1. Remove the product from selectedProducts
                                    const updatedProducts = selectedProducts.filter((p) => p.product_id !== removedProductId);
                                    setSelectedProducts(updatedProducts);

                                    // 2. Remove only that product's allocated units
                                    setAllocatedUnits((prev) => {
                                      if (!prev[removedProductId]) return prev; // nothing to delete

                                      const { [removedProductId]: _, ...rest } = prev; // safely omit one key
                                      return rest;
                                    });
                                  }}

                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="" className="table-headers">Instructions </label>
                <textarea
                  name="instructions"
                  id="instructions"
                  className="form-control"
                  placeholder="Add instructions here"
                  value={formik.values.instructions}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  readOnly={!isFieldEditable(installationOrder?.status ?? 0, "Instructions")}
                ></textarea>
                <div className="col-md-4 mt-2">
                  <label className="fw-semibold mb-1" htmlFor="localLaws">
                    Local Laws Compliance:
                  </label>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="localLaws"
                      name="localLawsChecked"
                      className="form-check-input"
                      checked={formik.values.localLawsChecked}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <label htmlFor="localLaws" className="form-check-label ms-2">
                      I acknowledge adherence to local laws
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-2"></div>

              <div className="col-md-4">
                {/* <table className="table table-bordered table-sm align-middle">
                  <tbody>
                    <tr>
                      <th className="bg-light table-headers">Total Units</th>
                      <td className="fw-semibold table-cells text-end">{totalUnits}</td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Discount</th>
                      <td className="table-cells text-end">
                        <div className="input-group input-group-sm justify-content-end" style={{ maxWidth: 120, marginLeft: "auto" }}>
                          <span className="input-group-text bg-light px-2">$</span>
                          <input
                            type="number"
                            name="discount"
                            id="discount"
                            className="form-control form-control-sm text-end"
                            value={Number(formik.values.discount).toFixed(2)}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>

                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Installation Charge</th>
                      <td className="table-cells text-end">
                        <div className="input-group input-group-sm justify-content-end" style={{ maxWidth: 120, marginLeft: "auto" }}>
                          <span className="input-group-text bg-light px-2">$</span>
                          <input
                            type="number"
                            name="installation_charge"
                            id="installation_charge"
                            className="form-control form-control-sm text-end"
                            value={Number(formik.values.installation_charge).toFixed(2)}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Tax</th>
                      <td className="table-cells text-end">
                        <div className="input-group input-group-sm justify-content-end" style={{ maxWidth: 120, marginLeft: "auto" }}>
                          <span className="input-group-text bg-light px-2">$</span>
                          <input
                            type="number"
                            name="tax"
                            id="tax"
                            className="form-control form-control-sm text-end"
                            value={Number(formik.values.tax).toFixed(2)}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Subtotal</th>
                      <td className="fw-semibold text-end table-cells">
                        ${Number(formik.values.subtotal).toFixed(2)}
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Total</th>
                      <td className="fw-bold text-success text-end table-cells">
                        ${Number(formik.values.total).toFixed(2)}
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Difference</th>
                      <td className="fw-bold text-success text-end table-cells">$0.00</td>
                    </tr>
                  </tbody>
                </table> */}
                <table className="table  table-sm align-middle">
                  <tbody>
                    <tr>
                      <th className="bg-light table-headers">Total Units</th>
                      <td className=" table-cells text-center">{totalUnits}</td>
                    </tr>

                    {/* <tr>
      <th className="bg-light table-headers">Discount</th>
      <td className="table-cells text-center">
        ${Number(formik.values.discount).toFixed(2)}
      </td>
    </tr> */}

                    <tr>
                      <th className="bg-light table-headers">Installation Cost</th>
                      <td className="table-cells text-center">
                        ${Number(formik.values.installation_charge).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-light table-headers">Subtotal</th>
                      <td className=" text-center table-cells">
                        ${Number(formik.values.subtotal).toFixed(2)}
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Tax</th>
                      <td className="table-cells text-center">
                        ${Number(formik.values.tax).toFixed(2)}
                      </td>
                    </tr>

                    <tr>
                      <th className="bg-light table-headers">Total</th>
                      <td className=" text-center table-cells">
                        ${Number(formik.values.total).toFixed(2)}
                      </td>
                    </tr>

                    {/* <tr>
      <th className="bg-light table-headers">Difference</th>
      <td className="fw-bold text-success text-center table-cells">
        $0.00
      </td>
    </tr> */}
                  </tbody>
                </table>

              </div>



            </div>

            {/* <div className="row mb-3">
              <div className="col-md-7">
                <label htmlFor="">Instructions </label>
                <textarea
                  name="instructions"
                  id="instructions"
                  className="form-control"
                  placeholder="Add instructions here"
                  value={formik.values.instructions}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  readOnly={!isFieldEditable(installationOrder?.status ?? 0, "Instructions")}
                ></textarea> */}

            {/* <div className="col-md-12 mt-5">
                  <label htmlFor="">Proof of Installation</label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    name="image"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const newPreviews = files.map((file) => ({
                        file,
                        url: URL.createObjectURL(file),
                      }));
                      setPreviewFiles((prev) => [...prev, ...newPreviews]);
                    }}
                  />

                  <button
                    type="button"
                    className="btn btn-success mt-3"
                    onClick={async () => {
                      try {
                        const uploads = await Promise.all(
                          previewFiles.map(async ({ file }) => {
                            const path = await uploadFile(file); // ✅ Reuse function
                            return {
                              file_name: file.name,
                              file_url: path,
                              content_type: file.type,
                              uploaded_date: new Date().toISOString(),
                            };
                          })
                        );

                        const allFiles = [...uploadedFileMeta, ...uploads];
                        const uniqueByName = allFiles.filter(
                          (file, index, self) =>
                            index === self.findIndex((f) => f.file_name === file.file_name)
                        );

                        setUploadedFileMeta(uniqueByName);
                        setPreviewFiles([]); // clear previews after upload
                        toast.success("Files uploaded successfully!");
                      } catch (err) {
                        console.error("Upload error:", err);
                        showError("Failed to upload files.");
                      }
                    }}
                  >
                    Upload
                  </button>

                  <div className="row mt-3">
                    {uploadedFileMeta.map((file, index) => (
                      <div key={index} className="col-md-3 position-relative mb-3">
                        <div
                          className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                          style={{
                            width: "20px",
                            height: "20px",
                            textAlign: "center",
                            cursor: "pointer",
                            zIndex: 2,
                          }}
                          onClick={() => {
                            setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                          }}
                        >
                          &times;
                        </div>
                        <div className="border p-2">
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${file.file_url.replace(/\\/g, "/")}`}
                            alt={file.file_name}
                            className="img-fluid"
                            style={{ height: "150px", objectFit: "cover", width: "100%" }}
                          />
                          <div className="small text-center mt-1 text-muted">{file.file_name}</div>
                        </div>
                      </div>
                    ))}

                    {previewFiles.map((item, index) => (
                      <div key={`preview-${index}`} className="col-md-3 position-relative mb-3">
                        <div
                          className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                          style={{
                            width: "20px",
                            height: "20px",
                            textAlign: "center",
                            cursor: "pointer",
                            zIndex: 2,
                          }}
                          onClick={() => {
                            URL.revokeObjectURL(item.url);
                            setPreviewFiles(previewFiles.filter((_, i) => i !== index));
                          }}
                        >
                          &times;
                        </div>
                        <div className="border p-2">
                          <img
                            src={item.url}
                            alt={`preview-${index}`}
                            className="img-fluid"
                            style={{ height: "150px", objectFit: "cover", width: "100%" }}
                          />
                          <div className="small text-center mt-1 text-muted">{item.file.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                </div> */}
            {/* </div>
            </div> */}

            {/* <div className="row mb-3">
              <div className="col-md-6">

              </div>
              <div className="col-md-6">
                <div style={mapContainerStyle}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={10}
                    center={selectedLocation || center}
                    onClick={handleMapClick}
                  >
                    {selectedLocation && <Marker position={selectedLocation} />}
                  </GoogleMap>
                </div>
              </div>

            </div> */}
            <div className="row mb-3 rcust-margin-4">
              <div className="col-md-12 d-flex justify-content-between align-items-center">
                <button
                  className="btn btn-outline-secondary br-8 backbutton"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                <button
                  type="submit"
                  className="btn btn-info mt-4 float-end"
                  disabled={formSubmitting || !formik.values.localLawsChecked}
                >
                  Save Changes
                </button>
                {(showAllocateModal || showViewInventoryModal) && (
                  <div className="modal-backdrop show"></div>
                )}
              </div>
            </div>

            {/* <div className="row justify-content-end"> */}
            {/* <div className="col-md-3 mt-3">
                <label className="me-1 mb-1">Local Laws Compliance :</label><br />
                <input
                  type="checkbox"
                  id="localLaws"
                  name="localLawsChecked"
                  checked={formik.values.localLawsChecked}
                  onChange={formik.handleChange}
                />
                <span>{" "}I acknowledge adherence to local laws</span>

              </div>
            </div>

            <button
              type="submit"
              className="btn btn-info mt-4 float-end"
              disabled={formSubmitting || !formik.values.localLawsChecked}
            >
              Save Changes
            </button> */}
          </form>
          {/* {(showAllocateModal || showViewInventoryModal) && (
            <div className="modal-backdrop show"></div>
          )} */}
          {/* <div className={`modal fade ${showAllocateModal ? "show d-block" : ""}`} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Allocate Rotational Product </h5>
                  <button type="button" className="btn-close" onClick={() => setShowAllocateModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4">
                      <label htmlFor="">Select Product:</label>
                    </div>
                    <div className="col-md-8">
                      <Select
                        name="productId"
                        id="productId"
                        options={rotationalproductOptions}
                        value={selectedProduct}
                        onChange={handleProductSelect}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <label htmlFor="">Select Warehouse:</label>
                    </div>
                    <div className="col-md-8">
                      <Select
                        name="warehouseId"
                        id="warehouseId"
                        options={warehouseOptions}
                        value={selectedWarehouse}
                        onChange={(selected) => {
                          setSelectedWarehouse(selected);
                          if (selectedProduct?.fullProduct && selected?.value) {
                            const rotationalId = selectedProduct.value;
                            const warehouseId = selected.value;
                            fetchSerializedUnits(rotationalId, warehouseId);
                          }
                        }}
                      />


                      <select className="form-control">
                        <option value="warehouse A">Warehouse A</option>
                        <option value="warehouse B">Warehouse B</option>
                      </select>
                    </div>
                  </div>
                  <p><strong>Quantity Required:</strong> 3</p>

                  * ✅ Check if this linked product already has one allocation
                  {(() => {
                    const hasAllocationForLinkedProduct = !!(linkedProductIdContext && allocatedUnits[linkedProductIdContext]);

                    return (
                      <table className="table table-bordered default-table">
                        <thead>
                          <tr>
                            <th>Serial No.</th>
                            <th>Warehouse</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableSerializedUnits?.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center">No units available</td>
                            </tr>
                          ) : (
                            availableSerializedUnits?.map((unit, idx) => (
                              <tr key={unit.serial_id || idx}>
                                <td>{unit.serial_number || "N/A"}</td>
                                <td>{unit.warehouse_name}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-success"
                                    disabled={hasAllocationForLinkedProduct}
                                    onClick={() => {
                                      if (!linkedProductIdContext || !selectedProduct || !selectedWarehouse) return;

                                      const linkedProductId = linkedProductIdContext;
                                      const rotationalProductId = selectedProduct.value;
                                      const warehouseId = selectedWarehouse.value;

                                      const newUnit = {
                                        rotationalProductId,
                                        warehouseId,
                                        serial_id: unit.serial_id,
                                        serial_number: unit.serial_number,
                                      };

                                      setAllocatedUnits((prev) => {
                                        const existing = prev[linkedProductId] || [];
                                        const alreadyExists = existing.some(u => u.serial_id === unit.serial_id);
                                        if (alreadyExists) {
                                          toast.warning("This unit is already allocated.");
                                          return prev;
                                        }
                                        return {
                                          ...prev,
                                          [linkedProductId]: [...existing, newUnit],
                                        };
                                      });

                                      toast.success("Unit allocated.");
                                    }}
                                  >
                                    Allocate
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    );
                  })()}

                </div>
              </div>
            </div>
          </div> */}

        </div>
      </div >
    </div >
  );
};

export default SignageEditInstallation;
